<template>
    <div class="text-h6 font-weight-regular">
        <v-layout :style="selectedPlan.status === 'pending' ? 'min-height: 18rem' : ''">
            <v-flex xs6 class="mr-5">
                <v-layout>
                    <v-flex xs4 grow>Name:</v-flex>
                    <v-tooltip bottom :disabled="!isEllipsed" content-class="plan-tooltip">
                        <template v-slot:activator="{ on }">
                            <v-flex data-testid="plan-name" class="plan-overflow tooltip"
                                v-on="on"
                                @mouseover="setIsEllipsed">{{ selectedPlan.first_name }} {{ selectedPlan.last_name }}</v-flex>
                        </template>
                        <span data-testid="plan-name">{{ selectedPlan.first_name }} {{ selectedPlan.last_name }}</span>
                    </v-tooltip>
                </v-layout>
                <v-layout class="my-3">
                    <v-flex xs4>Phone:</v-flex>
                    <v-flex data-testid="plan-phone">{{ addDashes(selectedPlan.phone) }}</v-flex>
                </v-layout>
                <v-layout class="my-3">
                    <v-flex xs4>Unit Number:</v-flex>
                    <v-flex data-testid="plan-unit-number">{{ selectedPlan.unit.name }}</v-flex>
                </v-layout>
                <v-layout class="my-3">
                    <v-flex xs4>Tenant ID:</v-flex>
                    <v-flex data-testid="plan-tenant-id">{{ selectedPlan.tenant_id }}</v-flex>
                </v-layout>
                <v-layout class="my-3">
                    <v-flex xs4>Facility:</v-flex>
                    <v-flex data-testid="plan-location-code">{{ selectedPlan.facility.location_code }}</v-flex>
                </v-layout>
                <v-layout class="my-3">
                    <v-flex xs4 grow>E-Mail:</v-flex>
                    <v-tooltip bottom :disabled="!isEllipsed" content-class="plan-tooltip">
                        <template v-slot:activator="{ on }">
                            <v-flex data-testid="plan-email" class="plan-overflow" v-on="on" @mouseover="setIsEllipsed">{{ selectedPlan.email }}</v-flex>
                        </template>
                        <span data-testid="plan-email">{{selectedPlan.email}}</span>
                    </v-tooltip>
                </v-layout>
            </v-flex>
            <v-flex xs6>
                <v-layout>
                    <v-flex xs4>Plan Status:</v-flex>
                    <v-flex style="text-transform: capitalize" data-testid="plan-status">{{ selectedPlan.status }}</v-flex>
                </v-layout>
                <v-layout class="my-3">
                    <v-flex xs4>Plan Created:</v-flex>
                    <v-flex data-testid="plan-created">{{ formatDate(selectedPlan.created_at) }}</v-flex>
                </v-layout>
                <div v-if="promiseToPay">
                    <v-layout class="my-3">
                        <v-flex xs4>Payment Date:</v-flex>
                        <v-flex data-testid="payment-date">{{ formatDate(selectedPlan.payment_date) }}</v-flex>
                    </v-layout>
                    <v-layout class="my-3">
                        <v-flex xs4>Payment Amount:</v-flex>
                        <v-flex data-testid="promise-payment-amount">
                            ${{ parseFloat(selectedPlan.payment_amount).toFixed(2) }}
                        </v-flex>
                    </v-layout>
                    <v-layout class="my-3">
                        <v-flex xs4>Payment Attempts:</v-flex>
                        <v-flex data-testid="payment-attempt">{{ selectedPlan.payment_attempt }}</v-flex>
                    </v-layout>
                </div>
                <div v-else>
                    <v-layout class="my-3">
                        <v-flex xs4>Plan Duration:</v-flex>
                        <v-flex data-testid="plan-duration">{{ selectedPlan.duration }} Months</v-flex>
                    </v-layout>
                    <v-layout class="my-3">
                        <v-flex xs4>Total Balance:</v-flex>
                        <v-flex data-testid="plan-balance">
                            ${{ parseFloat(selectedPlan.total_balance).toFixed(2) }}
                        </v-flex>
                    </v-layout>
                    <v-layout class="my-3">
                        <v-flex xs4>Monthly Rate:</v-flex>
                        <v-flex data-testid="plan-monthly-rate">
                            ${{ parseFloat(selectedPlan.monthly_rate).toFixed(2) }}/month
                        </v-flex>
                    </v-layout>
                    <v-layout class="my-3">
                        <v-flex xs4>Next Payment Date:</v-flex>
                        <v-flex data-testid="next-payment-date">
                            {{ findNextPaymentInfo(selectedPlan.payments).payment_date }}
                        </v-flex>
                    </v-layout>
                    <v-layout class="my-3">
                        <v-flex xs4>Payment Amount:</v-flex>
                        <v-flex data-testid="plan-payment-amount">
                            ${{ findNextPaymentInfo(selectedPlan.payments).payment_amount.toFixed(2) }}
                        </v-flex>
                    </v-layout>
                </div>
            </v-flex>
        </v-layout>
        <v-layout v-if="selectedPlan.status === 'pending'" style="min-height: 10rem">
            <transition name="slide-x-transition" mode="out-in">
                <v-flex
                    xs6
                    v-if="!showResendEmail"
                    class="pr-4"
                >
                    <v-layout class="success--text" v-show="confirmEmailSent">
                        Email sent {{ updateEmail ? 'and updated' : '' }} successfully.
                    </v-layout>
                    <v-btn
                        data-testid="resend-agreement-email-btn"
                        class="resent-agreement-email-btn mt-4"
                        block
                        color="teal lighten-1"
                        @click="showResendEmail = !showResendEmail"
                        key="toggle-resend"
                    >
                        Resend Agreement Email
                    </v-btn>
                </v-flex>
                <v-layout
                    v-else
                    key="resend-email"
                    class="mt-3"
                >
                    <v-flex xs6 class="pr-4">
                        <v-text-field
                            data-testid="resend-email-input"
                            class="resend-email-input"
                            v-model="agreementEmail"
                            filled
                            label="Email Address"
                            hide-details
                        />
                        <transition name="scroll-y-reverse-transition" mode="out-in">
                            <v-checkbox
                                data-testid="update-email-checkbox"
                                class="update-email-checkbox mt-4 mb-2"
                                v-model="updateEmail"
                                v-show="agreementEmail !== selectedPlan.email"
                                hide-details
                            >
                                <template v-slot:label>
                                    <div class="subheading">
                                        {{ updateEmail ? 'Will be updated' : 'Update' }} as primary e-mail address
                                    </div>
                                </template>
                            </v-checkbox>
                        </transition>
                    </v-flex>
                    <v-flex xs2 class="mt-2 pt-1">
                        <v-btn
                            data-testid="resend-email-btn"
                            class="resend-email-btn ml-0 mr-3"
                            color="teal lighten-1"
                            fab
                            bottom
                            @click.native="handleResendEmail"
                            :disabled="!validEmail"
                        >
                            <v-icon>mail_outline</v-icon>
                        </v-btn>
                        <v-btn
                            data-testid="close-resend-email-btn"
                            class="close-resend-email-btn mx-0"
                            color="red darken-2"
                            fab
                            bottom
                            @click="showResendEmail = !showResendEmail"
                        >
                            <v-icon>close</v-icon>
                        </v-btn>
                    </v-flex>
                </v-layout>
            </transition>
        </v-layout>
    </div>
</template>

<script>
import moment from "moment";

export default {
  name: "PlanInfo",
  props: {
    selectedPlan: {
      type: [Object, null],
      default: null
    },
    promiseToPay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      agreementEmail: "",
      agreementEmailEntered: false,
      confirmEmailSent: false,
      showResendEmail: false,
      updateEmail: false,
      isEllipsed: false
    };
  },
  methods: {

    setIsEllipsed(e) {
      this.isEllipsed = e.target.offsetWidth < e.target.scrollWidth;
    },

    formatDate(date) {
      return moment.utc(date).format("MM/DD/YYYY");
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    findNextPaymentInfo(payments) {
      if (payments.length) {
        const futurePayments = payments.filter(payment => moment(payment.payment_date).isAfter());
        const nearestFuturePayment = futurePayments[0];
        return {
          "payment_amount": nearestFuturePayment.payment_amount,
          "payment_date": moment(nearestFuturePayment.payment_date).format("dddd, MMMM Do YYYY")
        };
      } else {
        return "N/A";
      }
    },

    handleResendEmail() {
      const sendAction = this.promiseToPay
        ? "resendScheduledPaymentAgreementEmail"
        : "resendPaymentPlanAgreementEmail";
      this.resendLoading = true;
      const payload = {
        id: this.selectedPlan.id,
        email_address: this.agreementEmail,
        update_email: this.updateEmail
      };
      this.$store.dispatch(sendAction, payload)
        .then(() => {
          this.resendLoading = false;
          this.confirmEmailSent = true;
          this.showResendEmail = false;
          setTimeout(() => { this.confirmEmailSent = false; this.updateEmail = false; }, 3000);
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.resendLoading = false;
        });
    }
  },
  computed: {
    validEmail() {
      let regex = /^\S+@\S+\.\S+$/;
      return regex.test(this.agreementEmail);
    }
  },
  mounted() {
    this.agreementEmail = this.selectedPlan.email;
  }
};
</script>

<style scoped>
.plan-overflow {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
